import { X } from "@tamagui/lucide-icons";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Platform, TextInput } from "react-native";

import { useUpdateEvent } from "@/api/mutations/conversation";
import {
  Adapt,
  Button,
  Dialog,
  H1,
  type InputKeyPressEvent,
  Sheet,
  TextArea,
  XStack,
} from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";

export default function FeedbackCommentModal() {
  const {
    lg,
    feedbackModalData,
    feedbackModalOpen: open,
    setFeedbackModalOpen,
    setFeedbackModalData,
  } = useContext(LayoutContext);
  const { messageId, initialComment } = feedbackModalData;
  const { mutate: updateEvent } = useUpdateEvent();

  const [comment, setComment] = useState(initialComment || "");
  const textAreaRef = useRef<TextInput>(null);

  // Sync comment state when modal opens with initial comment
  useEffect(() => {
    if (open) {
      setComment(initialComment || "");
    }
  }, [open, initialComment]);

  // Focus the textarea when modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        textAreaRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    updateEvent({ eventId: messageId!, data: { comment } });
    setFeedbackModalData({});
    setFeedbackModalOpen(false);
  }, [
    comment,
    setFeedbackModalData,
    messageId,
    updateEvent,
    setFeedbackModalOpen,
  ]);

  const handleClose = useCallback(() => {
    setFeedbackModalData({});
    setFeedbackModalOpen(false);
  }, [setFeedbackModalData, setFeedbackModalOpen]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
    >
      <Adapt when="maxLg">
        <Sheet
          modal
          open={open}
          onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
          disableDrag
        >
          <Sheet.Frame padding="$300" gap="$200">
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
        <Dialog.Content
          borderRadius="$150"
          padding="$300"
          minWidth={400}
          maxWidth={500}
          gap="$200"
        >
          {/* Header with close button */}
          <XStack justifyContent="space-between" alignItems="center">
            <H1 id="feedback-modal-heading" size="$lg" $lg={{ size: "$xl" }}>
              Leave feedback
            </H1>
            <Dialog.Close asChild displayWhenAdapted>
              <Button.Wrapper aria-label="Close" onPress={handleClose}>
                <X size={24} />
              </Button.Wrapper>
            </Dialog.Close>
          </XStack>

          <TextArea
            ref={textAreaRef}
            aria-labelledby="feedback-modal-heading"
            placeholder={
              lg
                ? "Share your thoughts... (Shift+Enter for newline)"
                : "Share your thoughts..."
            }
            value={comment}
            onChangeText={setComment}
            onKeyPress={(e: InputKeyPressEvent) => {
              if (Platform.OS !== "web") return;
              const { key, shiftKey } = e.nativeEvent;
              if (key === "Enter" && ((lg && !shiftKey) || (!lg && shiftKey))) {
                e.preventDefault?.();
                handleSubmit();
              }
              if (lg && key === "Escape") {
                e.preventDefault?.();
                handleClose();
              }
            }}
            selection={
              comment
                ? { start: comment.length, end: comment.length }
                : undefined
            }
            numberOfLines={6}
            fontSize="$200"
            frameProps={{ borderColor: "$fillNeutral", maxHeight: 300 }}
          />

          {/* Submit button */}
          <XStack justifyContent="flex-end" gap="$100">
            <Button mode="ghost" onPress={handleClose}>
              Cancel
            </Button>
            <Button mode="primary" onPress={handleSubmit}>
              {initialComment && !comment.trim() ? "Clear feedback" : "Submit"}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
